import React, { useState, useEffect } from "react";
import BitrixAPI from "../api/b24";
import FilePreview from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import GlassDetailsModal from "../components/GlassDetailsModal";
import { useHeader } from "../contexts/HeaderContext";

const MatovkaOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [error, setError] = useState(null);

  // Состояние для polling новых заказов
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [newOrdersNotification, setNewOrdersNotification] = useState(null); // {count: number, orderNumbers: string[]}

  // Состояния для деталировки
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { setHeaderExtras } = useHeader();
  
  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : 'bg-blue-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : 'bg-blue-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для завершения' : 'Свайп для завершения →'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      const result = await BitrixAPI.updateStagematovka(orderId);
      
      if (result && result.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        console.log('Заказ успешно переведен из матовки');
      } else {
        throw new Error(result?.message || "Не удалось обновить стадию");
      }
    } catch (error) {
      console.error("Ошибка при обновлении стадии:", error);
      setError(`Ошибка при обновлении заказа ${orderId}: ${error.message}`);
      alert(`Ошибка: ${error.message}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Функция для открытия модального окна деталировки
  const handleOpenGlassDetails = (order) => {
    setSelectedOrderForDetails(order);
    setDetailsModalOpen(true);
  };

  // Функция для закрытия модального окна
  const handleCloseGlassDetails = () => {
    setDetailsModalOpen(false);
    setSelectedOrderForDetails(null);
  };

  // Функция для загрузки заказов (выделена для повторного использования)
  const fetchOrders = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const response = await BitrixAPI.getActiveOrdersmatovka();

      if (response && Array.isArray(response)) {
        // Определяем новые заказы (которых еще нет в текущем списке)
        const newOrders = response.filter(newOrder =>
          !orders.some(existingOrder => existingOrder.id === newOrder.id)
        );

        if (newOrders.length > 0 && !isInitialLoad) {
          console.log(`✨ Обнаружено ${newOrders.length} новых заказов матовки:`, newOrders.map(o => o.id));

          // Показываем уведомление о новых заказах
          setNewOrdersNotification({
            count: newOrders.length,
            orderNumbers: newOrders.map(o => o.orderNumber || o.id)
          });

          // Воспроизводим звук уведомления (если доступен)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE=');
            audio.play().catch(err => console.log('Не удалось воспроизвести звук:', err));
          } catch (err) {
            console.log('Ошибка создания аудио:', err);
          }

          // Автоматически скрываем уведомление через 10 секунд
          setTimeout(() => {
            setNewOrdersNotification(null);
          }, 10000);
        }

        setOrders(response || []);
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов:", err);
      setError(`Ошибка загрузки заказов: ${err.message}`);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{orders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchQuery, orders.length]);

  // Первоначальная загрузка заказов
  useEffect(() => {
    fetchOrders(true);
  }, []);

  // Polling для автоматического обновления заказов
  useEffect(() => {
    if (!isPollingActive) {
      return;
    }

    const pollingInterval = setInterval(() => {
      console.log('🔄 Проверка новых заказов матовки...');
      fetchOrders(false);
    }, 30000); // Проверяем каждые 30 секунд

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isPollingActive, orders]);

  if (loading) return <div className="p-4">Загрузка заказов...</div>;

  return (
    <div className="container mx-auto p-4">
      

      {/* Панель управления и статистика */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-3">
          

          

        </div>
      </div>

      {/* Уведомление о новых заказах */}
      {newOrdersNotification && (
        <div className="bg-green-50 border-2 border-green-400 text-green-800 px-4 py-3 rounded-lg mb-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <strong className="text-lg">Поступило {newOrdersNotification.count} новых заказов матовки!</strong>
                <div className="text-sm text-green-700 mt-1">
                  Заказы: {newOrdersNotification.orderNumbers.join(', ')}
                </div>
              </div>
            </div>
            <button
              onClick={() => setNewOrdersNotification(null)}
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      <div className="space-y-4">
        {orders && orders.length > 0 ? (
          orders.filter((order) => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            return (
              String(order.orderNumber || "").toLowerCase().includes(q) ||
              String(order.title || "").toLowerCase().includes(q) ||
              String(order.id || "").toLowerCase().includes(q)
            );
          }).map((order) => (
            <div 
              key={order.id} 
              className="bg-white p-4 shadow rounded-lg transform transition-all duration-300 hover:shadow-lg"
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-medium">Заказ №{order.orderNumber}</h2>
                  <p className="text-gray-600">{order.title}</p>
                  <p className="text-xs text-gray-500">ID: {order.id}</p>
                </div>
                
                {/* Контейнер для кнопок */}
                <div>
                  {/* КНОПКА ДЕТАЛИРОВКИ */}
                  <button
                    onClick={() => handleOpenGlassDetails(order)}
                    className="glass-details-btn bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-2 rounded flex items-center space-x-1 transition-colors duration-200"
                    title={`Открыть деталировку для заказа ${order.orderNumber}`}
                  >
                    <span>📋</span>
                    <span className="hidden sm:inline">Деталировка матовки</span>
                  </button>
                </div>
              </div>

              {order.productionFiles && order.productionFiles.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                  {order.productionFiles.map((file) => (
                    <FilePreview 
                      key={file.id} 
                      file={file} 
                      dealId={order.id}
                    />
                  ))}
                </div>
              )}

              {order.files && order.files.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                  {order.files.map((file) => (
                    <FilePreview 
                      key={file.id} 
                      file={file} 
                      dealId={order.id}
                    />
                  ))}
                </div>
              )}

              {/* Свайп-кнопка во всю ширину блока */}
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={handleStageUpdate}
                  isLoading={processingOrders.has(order.id)}
                />
              </div>

            </div>
          ))
        ) : (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <p className="text-gray-600">Нет заказов для отображения</p>
          </div>
        )}
      </div>

      {/* Модальное окно деталировки */}
      {detailsModalOpen && selectedOrderForDetails && (
        <GlassDetailsModal
          orderId={selectedOrderForDetails.id}
          orderNumber={selectedOrderForDetails.ufCrm3_1746789101}
          orderTitle={selectedOrderForDetails.title}
          stage="matovka"
          onClose={handleCloseGlassDetails}
        />
      )}
    </div>
  );
};

export default MatovkaOrdersPageB24;